import { collabTag } from './monster';

/**
 * 몹 한 칸(가문 × 속성)을 만드는 규칙 — **보유몹과 방덱이 같은 열쇠를 써야 한다.**
 *
 * 보유몹 화면에서 켜 둔 몹으로 방덱 배치를 판단하므로, 두 화면이 같은 방식으로 열쇠를
 * 만들지 않으면 "가진 몹인데 못 세운다"가 된다. 그래서 규칙을 여기 한 곳에만 둔다
 * (서버의 `roster.ts` · `collabImage.ts` 와 짝을 맞춘 규칙이다).
 */

export const ATTRS = [
    { key: 'Water', label: '물', img: '/images/water.png' },
    { key: 'Fire', label: '불', img: '/images/fire.png' },
    { key: 'Wind', label: '풍', img: '/images/wind.png' },
    { key: 'Light', label: '빛', img: '/images/light.png' },
    { key: 'Dark', label: '암', img: '/images/dark.png' }
] as const;

/** 몹 한 칸 */
export interface Cell {
    /**
     * `가문|속성` — **서버에 저장하는 열쇠**.
     *
     * 콜라보 짝은 한 열쇠로 모으므로(`canonicalFamily`) 두 칸이 같은 값을 가질 수 있다.
     * 그래서 이걸 React 의 `key` 로 쓰면 안 된다 — 아래 `uid` 를 쓴다.
     */
    key: string;
    /**
     * 화면에서 이 칸을 짚는 값. 보이는 가문 이름으로 만들어 **겹치지 않는다.**
     *
     * 예전에 `key` 를 React key 로 썼다가 콜라보 짝에서 값이 겹쳤다. 그러면 React 가
     * 목록을 다시 그릴 때 어느 칸이 어느 것인지 잃어버려서, **속성·성급을 눌러도 화면이
     * 제대로 바뀌지 않았다.**
     */
    uid: string;
    family: string;
    attribute: string;
    /** 화면에 쓸 이름(별칭이 있으면 별칭) */
    label: string;
    /** 태생 성급 */
    natural: number;
    imgPath: string | null;
}

/**
 * 태생 성급.
 *
 * 각성하면 한 성급 올라가므로 **각성 전(Unawaken) 값**이 태생이다. 각성 전 정보가 없는
 * 칸도 있어서(42칸) 그때는 각성 값에서 하나를 뺀다.
 */
export function naturalStars(byAttr: any): number | null {
    const un = byAttr?.Unawaken?.stars;
    if (typeof un === 'number') return un;
    const aw = byAttr?.Awakened?.stars;
    return typeof aw === 'number' ? aw - 1 : null;
}

/** 그림은 각성 후를 쓴다 — 목록에서 그게 알아보기 쉽다 */
export function cellImage(byAttr: any): string | null {
    return byAttr?.SecondAwakened?.filepath
        || byAttr?.Awakened?.filepath
        || byAttr?.Unawaken?.filepath
        || null;
}

/**
 * 칸에 적을 이름 — **한글 별칭 중 가장 짧은 것.**
 *
 * 별칭을 그냥 첫 번째로 쓰면 `7r1x`, `570rm` 같은 영문·리트 표기가 잡힌다(매핑에 검색용
 * 별칭이 여러 개 들어 있다). 한글을 고르는 이유는 목록에서 몹을 눈으로 찾는 이름이라
 * 한글이 훨씬 빠르기 때문이고, **짧은 쪽**을 고르는 이유는 칸이 좁아서다 — 같은 몹에
 * `물의 정령 티아나`·`물티아나`·`물티` 가 함께 들어 있으면 긴 이름은 잘려 나간다.
 */
export function pickLabel(aliases: string[], family: string): string {
    let best: string | null = null;
    for (const a of aliases) {
        if (!/[가-힣]/.test(a)) continue;
        if (best === null || a.length < best.length) best = a;
    }
    return best || aliases[0] || family;
}

/**
 * 저장에 쓸 가문 이름.
 *
 * 콜라보 짝은 화면에서 한 칸으로만 보여주는데, **어느 쪽을 보여줄지는 `isCollab` 표시에
 * 따라 바뀔 수 있다**(사람이 사이트에서 고친다). 보이는 쪽 이름으로 저장하면, 표시를
 * 뒤집는 순간 저장해 둔 열쇠가 감춰진 가문을 가리켜 값이 통째로 사라진다.
 *
 * 그래서 **짝 중 이름이 앞선 쪽으로 고정한다** — 표시가 뒤집혀도 열쇠는 그대로다.
 */
export function canonicalFamily(mapping: any, family: string): string {
    const partner = mapping?.monsters?.[family]?.collab;
    if (!partner || !mapping?.monsters?.[partner]) return family;
    return family <= partner ? family : partner;
}

/** 가문이 가진 가장 작은 `unit_master_id`. 없으면 null */
function minUnitId(info: any): number | null {
    let min: number | null = null;
    for (const a of ATTRS) {
        const byAttr = info?.[a.key];
        if (!byAttr) continue;
        for (const st of ['Unawaken', 'Awakened', 'SecondAwakened']) {
            const raw = byAttr?.[st]?.unit_master_id;
            for (const v of Array.isArray(raw) ? raw : [raw]) {
                const n = Number(v);
                if (Number.isFinite(n) && (min === null || n < min)) min = n;
            }
        }
    }
    return min;
}

/**
 * 콜라보 짝 중 **화면에 보여줄 쪽**. 짝이 없으면 자기 자신.
 *
 * 콜라보 리스킨은 겉모습만 다른 같은 몹이라 한 칸으로만 보여야 한다. 예전에는
 * `hiddenByCollab`(사람이 지정하는 `isCollab` 표시)에 기댔는데, **표시가 없는 짝이 많아서
 * 두 칸으로 보였다**(개발용 매핑에는 하나도 없다).
 *
 * 그래서 반반 그림의 좌우를 정할 때와 **같은 규칙**을 쓴다(backend `collabImage.collabSide`).
 *   1. 사람이 콜라보라고 표시한 쪽
 *   2. `unit_master_id` 가 작은 쪽 — 콜라보가 먼저 나오고 상시 리스킨이 나중에 붙는다
 *   3. 그래도 모르면 이름 순
 * 그림의 왼쪽 절반이 콜라보이므로, 이름도 콜라보 쪽으로 맞춰야 보는 사람이 헷갈리지 않는다.
 */
export function displayFamily(mapping: any, family: string): string {
    const mons = mapping?.monsters;
    const partner = mons?.[family]?.collab;
    if (!partner || !mons?.[partner]) return family;

    const me = mons[family];
    const you = mons[partner];
    if (me.isCollab === true && you.isCollab !== true) return family;
    if (you.isCollab === true && me.isCollab !== true) return partner;

    const a = minUnitId(me);
    const b = minUnitId(you);
    if (a !== null && b !== null && a !== b) return a < b ? family : partner;

    return family <= partner ? family : partner;
}

/**
 * 태생 성급이 `naturals` 인 칸 전부 (콜라보 짝은 한 칸으로).
 *
 * 기본은 **태생 4~5성**이다 — 보유몹은 그 아래를 세지 않는다(1,140칸 → 728칸). 방덱은
 * 3성 이하도 덱에 넣을 수 있어서(모두 가진 것으로 본다) 성급을 넓혀 부른다.
 */
export function buildCells(mapping: any, naturals: number[] = [4, 5]): Cell[] {
    if (!mapping?.monsters) return [];
    const out: Cell[] = [];
    for (const [family, info] of Object.entries<any>(mapping.monsters)) {
        if (displayFamily(mapping, family) !== family) continue;
        for (const a of ATTRS) {
            const byAttr = info?.[a.key];
            if (!byAttr) continue;
            const natural = naturalStars(byAttr);
            if (natural === null || !naturals.includes(natural)) continue;
            out.push({
                key: `${canonicalFamily(mapping, family)}|${a.key}`,
                uid: `${family}|${a.key}`,
                family,
                attribute: a.key,
                label: pickLabel(byAttr.aliases || [], family),
                natural,
                imgPath: withCollabTag(cellImage(byAttr), info?.collab)
            });
        }
    }
    return out;
}

/**
 * **모두 가진 것으로 보는 몹**인가 — 태생 3성 이하.
 *
 * 3성 이하는 아무나 쉽게 얻고 여러 마리씩 갖고 있어서, 보유몹에서 세지도 않는다. 방덱에
 * 들어가면 "가진 사람만"을 따질 것도 없이 누구나 세울 수 있다고 본다(마리 수도 안 따진다).
 */
export function freeStar(natural: number | null | undefined): boolean {
    return typeof natural === 'number' && natural <= 3;
}

/**
 * 짝이 있으면 그림 경로에 **짝 표**를 붙인다 — `getMonsterImgPath` 와 같은 규칙.
 *
 * 콜라보 몹은 서버가 반반 그림으로 합쳐 같은 주소로 내보낸다. 표를 붙여야 짝이 바뀔 때만
 * 주소가 바뀌어 30일 캐시를 줄 수 있다.
 *
 * **두 길이 같은 주소를 만들어야 한다.** 예전에는 이 쪽(방덱·보유몹·룬 탭)만 표를 안 붙여서
 * 같은 몹을 주소 두 가지로 받아 브라우저가 두 벌을 들고 있었다.
 */
function withCollabTag(imgPath: string | null, mate: string | undefined): string | null {
    return imgPath && mate ? `${imgPath}?c=${collabTag(String(mate))}` : imgPath;
}

/** 몹 그림 주소. 그림이 없는 칸도 있어서 빈 그림으로 대신한다 */
export function mobImgUrl(imgPath: string | null | undefined): string {
    return imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : '/images/empty.png';
}
